/**
 * Enhanced Supabase configuration with performance optimization
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';

import { getSupabaseAnonKey, getSupabaseUrl } from './index';

// Type for Supabase client configuration
interface SupabaseClientConfig {
  auth: {
    autoRefreshToken: boolean;
    persistSession: boolean;
    detectSessionInUrl: boolean;
  };
}

// Type for database error objects
interface DatabaseErrorObject {
  code?: string;
  error_code?: string;
  message?: string;
}

// Simple Supabase client instance
let supabaseClient: SupabaseClient | null = null;

/**
 * Initialize Supabase client with configuration
 */
export const initializeSupabase = (
  supabaseUrl: string,
  supabaseAnonKey: string
): SupabaseClient => {
  if (!supabaseClient) {
    const clientConfig: SupabaseClientConfig = {
      auth: {
        autoRefreshToken: true,
        persistSession: true, // Enable Supabase's built-in persistence for auto-refresh to work
        detectSessionInUrl: true,
      },
    };

    supabaseClient = createClient(supabaseUrl, supabaseAnonKey, clientConfig);
  }
  return supabaseClient;
};

/**
 * Create a client with custom auth token for request-scoped auth
 * This is necessary for Cloudflare Workers where setSession doesn't work
 */
export const createClientWithAuth = (authToken: string): SupabaseClient => {
  const supabaseUrl = getSupabaseUrl();
  const supabaseAnonKey = getSupabaseAnonKey();

  return createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
      detectSessionInUrl: false,
    },
    global: {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    },
  });
};

/**
 * Get the current Supabase client instance
 */
export const getSupabaseClient = (): SupabaseClient => {
  if (!supabaseClient) {
    throw new Error(
      'Supabase client not initialized. Call initializeSupabase() first.'
    );
  }
  return supabaseClient;
};

// Initialize and export the default supabase client
export const supabase = initializeSupabase(
  getSupabaseUrl(),
  getSupabaseAnonKey()
);

/**
 * Reset the Supabase client (for testing only)
 */
export const resetSupabaseClient = (): void => {
  supabaseClient = null;
};

/**
 * Database table names for consistency
 */
export const TABLES = {
  ACCOUNT_PERFORMANCE: 'account_performance',
  ACCOUNT_PROCESSING_LOG: 'account_processing_log',
  ACCOUNTS: 'accounts',
  ACCOUNTS_WITH_PLAID_DETAILS: 'accounts_with_plaid_details',
  EXTENDED_HOLDINGS: 'extended_holdings',
  GLOBAL_SECURITIES: 'global_securities',
  GLOBAL_SECURITIES_PRICE_HISTORY: 'global_securities_price_history',
  GLOBAL_SECURITIES_SYNC_STATUS: 'global_securities_sync_status',
  GOAL_ANALYSES: 'goal_analyses',
  GOAL_MILESTONES: 'goal_milestones',
  GOAL_PERFORMANCE: 'goal_performance',
  GOAL_SUMMARY: 'goal_summary',
  GOALS: 'goals',
  HOLDINGS: 'holdings',
  HOLDINGS_PLAID: 'holdings_plaid',
  HOLDINGS_WITH_SECURITIES: 'holdings_with_securities',
  IDEA_SUBSCRIPTIONS: 'idea_subscriptions',
  IDEAS: 'ideas',
  ONBOARDING_PROGRESS: 'onboarding_progress',
  PLAID_ACCOUNTS: 'plaid_accounts',
  PLAID_DAILY_LOT_HOLDINGS: 'plaid_daily_lot_holdings',
  PLAID_DAILY_LOT_HOLDINGS_AGGREGATED: 'plaid_daily_lot_holdings_aggregated',
  PLAID_HOLDINGS: 'plaid_holdings',
  PLAID_ITEMS: 'plaid_items',
  PLAID_SECURITIES: 'plaid_securities',
  PLAID_TRANSACTIONS: 'plaid_transactions',
  PLAID_TRANSACTIONS_WITH_SECURITY_DATA:
    'plaid_transactions_with_security_data',
  PORTFOLIO_HISTORY: 'portfolio_history',
  QUEUE_METRICS: 'queue_metrics',
  TRANSACTIONS: 'transactions',
  USER_ALERTS: 'user_alerts',
  USER_PREFERENCES: 'user_preferences',
  USER_PROFILES: 'user_profiles',
  USER_SESSIONS: 'user_sessions',
  USERS: 'users',
} as const;

/**
 * Common database error types
 */
export enum DatabaseError {
  NOT_FOUND = 'NOT_FOUND',
  DUPLICATE_KEY = 'DUPLICATE_KEY',
  FOREIGN_KEY_VIOLATION = 'FOREIGN_KEY_VIOLATION',
  CHECK_VIOLATION = 'CHECK_VIOLATION',
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Parse Supabase error and return standardized error type
 */
export const parseDatabaseError = (
  error: DatabaseErrorObject | null | undefined
): DatabaseError => {
  if (!error) return DatabaseError.UNKNOWN;

  const code = error.code || error.error_code;
  const message = error.message || '';

  switch (code) {
    case 'PGRST116':
      return DatabaseError.NOT_FOUND;
    case '23505':
      return DatabaseError.DUPLICATE_KEY;
    case '23503':
      return DatabaseError.FOREIGN_KEY_VIOLATION;
    case '23514':
      return DatabaseError.CHECK_VIOLATION;
    case 'PGRST301':
      return DatabaseError.PERMISSION_DENIED;
    default:
      if (message.includes('not found')) {
        return DatabaseError.NOT_FOUND;
      }
      if (message.includes('duplicate key')) {
        return DatabaseError.DUPLICATE_KEY;
      }
      if (message.includes('violates foreign key')) {
        return DatabaseError.FOREIGN_KEY_VIOLATION;
      }
      return DatabaseError.UNKNOWN;
  }
};

/**
 * Health check for database connection
 */
export const checkDatabaseHealth = async (): Promise<boolean> => {
  try {
    const client = getSupabaseClient();
    const { error: _error } = await client
      .from(TABLES.USERS)
      .select('id')
      .limit(1);

    return !_error;
  } catch {
    return false;
  }
};

// Re-export enhanced service and query-related functionality
export {
  EnhancedSupabaseService,
  createEnhancedSupabaseService,
  defaultEnhancedSupabaseService,
  type QueryMetrics,
  type ConnectionPoolMetrics,
} from './supabaseQueries';
